import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubscribeForm from "@/components/feature/SubscribeForm";
import { usePageMeta } from "@/hooks/usePageMeta";
import { contentPrinciples, siteInfo, testimonials } from "@/mocks/forecast";
import { aboutIntro, milestones, positioningPoints } from "@/mocks/about";

export default function About() {
  usePageMeta({
    title: "关于我们 - 看料大瓜 | 预告前瞻的内容原则与联系方式",
    description:
      "了解看料大瓜的网站定位、发展理念与内容原则：我们只整理公开可见的信号，用时间线与预警帮助用户提前关注事件走向，并提供线索与纠错联系方式。",
    keywords: "看料大瓜,关于看料大瓜,前瞻内容原则,爆料线索联系,瓜田预警方法",
    path: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于看料大瓜",
        description: "看料大瓜的网站定位、内容原则与联系方式。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        description: "专注娱乐与商业领域爆料预告前瞻的内容平台。",
        email: siteInfo.email,
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系看料大瓜",
        description: "线索投稿与内容纠错的联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
        ],
      },
    ],
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-16">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
          <p className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-primary-600">
            <span className="h-px w-6 bg-primary-400" />
            About 看料大瓜
          </p>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
            我们不做转述，只把信号整理清楚
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            看料大瓜是一个专注预告前瞻的信息整理平台。我们的工作不是制造热闹，而是让每一个正在发酵的线索都能被看清来龙去脉。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="overflow-hidden rounded-lg border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20illustration%20of%20an%20abstract%20signal%20radar%20and%20timeline%20dots%20connected%20by%20thin%20lines%2C%20warm%20cream%20paper%20background%2C%20coral%20red%20and%20melon%20green%20accents%2C%20subtle%20grain%20texture%2C%20minimal%20geometric%20composition%2C%20no%20text%2C%20modern%20magazine%20aesthetic&width=1200&height=900&seq=gossip-about-story-01&orientation=landscape"
                  alt="看料大瓜信号整理方法的抽象插画"
                  className="h-72 w-full object-cover object-top md:h-96"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={100}>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">品牌介绍</h2>
              {aboutIntro.map((paragraph, index) => (
                <p
                  key={`intro-${index}`}
                  className={`text-sm leading-[1.9] text-foreground-700 md:text-base ${
                    index === 0 ? "mt-4" : "mt-4"
                  }`}
                >
                  {paragraph}
                </p>
              ))}
              <div className="mt-7 grid grid-cols-2 gap-5 border-t border-background-200 pt-6 sm:grid-cols-3">
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-600">6</p>
                  <p className="mt-1 text-xs text-foreground-500">监测领域</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-600">24h</p>
                  <p className="mt-1 text-xs text-foreground-500">信号同步</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-600">100%</p>
                  <p className="mt-1 text-xs text-foreground-500">判断依据公开</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              看料大瓜不是新闻站，也不是评论区。我们把自己定位成一个「信号整理工具」，用统一的格式呈现各个圈子的动向。
            </p>
          </Reveal>
          <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-3">
            {positioningPoints.map((point, index) => (
              <Reveal key={point.title} delay={index * 90}>
                <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-2xl text-accent-700">
                    <i className={point.icon} />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{point.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{point.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              下面四条是我们在整理每一条前瞻时都会遵守的底线。它们决定了我们不会做什么。
            </p>
          </Reveal>
          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {contentPrinciples.map((principle, index) => (
              <Reveal key={principle.title} delay={index * 80}>
                <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-800">
                    <i className={principle.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{principle.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{principle.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展理念与历程</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              方法不是一开始就完善的，它是在一次次判断与复盘里长出来的。
            </p>
          </Reveal>
          <ol className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-4 md:gap-5">
            {milestones.map((milestone, index) => (
              <Reveal as="li" key={milestone.title} delay={index * 90}>
                <div className="flex h-full flex-col border-t-2 border-primary-500 pt-5">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                    {milestone.year}
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{milestone.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{milestone.desc}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {testimonials.slice(0, 2).map((item, index) => (
              <Reveal key={item.name} delay={index * 90}>
                <div className="rounded-lg border border-accent-200 bg-accent-50 p-6">
                  <p className="text-sm leading-relaxed text-accent-950">「{item.quote}」</p>
                  <p className="mt-4 text-xs font-semibold text-accent-800">
                    {item.name} · {item.role}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="scroll-mt-24 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <Reveal>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{siteInfo.replyNote}</p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-mail-line" />
                  {siteInfo.email}
                </a>
                <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                  <i className="ri-information-line mt-0.5" />
                  投稿线索时，请尽量附上可公开查阅的来源链接，方便我们核实后整理成前瞻。
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={100}>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">订阅蹲守提醒</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  不想每次手动刷新，就留下邮箱。新的前瞻发布时，我们会第一时间提醒你。
                </p>
                <div className="mt-5">
                  <SubscribeForm />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-16 md:px-6">
          <div className="flex flex-col items-center justify-between gap-4 rounded-lg bg-primary-500 px-6 py-8 text-center md:flex-row md:text-left">
            <div>
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">准备好提前蹲守了吗？</h2>
              <p className="mt-1.5 text-sm text-primary-50">挑一个你常关注的方向，看看现在有哪些信号正在发酵。</p>
            </div>
            <Link
              to="/forecast"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-secondary-200"
            >
              <i className="ri-radar-line" />
              浏览预告前瞻
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}